import { Navigation } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { TRIP_STATUS, TH, TD } from './config';
import type { TripRow } from './hooks';
import type { Vehicle } from '../../types';

interface Props {
  trips: TripRow[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  summary: { tripsThisMonth: number; kmThisMonth: number; deductionsThisMonth: number };
  vehicles: Vehicle[];
  vehicleFilter: string | null;
  setVehicleFilter: (id: string | null) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export function TripsTab({ trips, loading, hasMore, loadMore, summary, vehicles, vehicleFilter, setVehicleFilter }: Props) {
  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Trips this month" value={String(summary.tripsThisMonth)} />
        <MetricCard label="Distance this month" value={`${summary.kmThisMonth.toLocaleString()} km`} />
        <MetricCard label="Mileage deductions" value={fmt$(summary.deductionsThisMonth)} />
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={vehicleFilter ?? ''} onChange={e => setVehicleFilter(e.target.value || null)}
          style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700, background: palette.white }}>
          <option value="">All Vehicles</option>
          {vehicles.filter(v => v.status !== 'retired').map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      {trips.length === 0 && !loading ? (
        <EmptyState icon={<Navigation size={22} />} title="No trips recorded" subtitle="Trips logged from the mobile app will appear here." />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Date</th><th style={TH}>Driver</th><th style={TH}>Vehicle</th>
            <th style={TH}>Route</th><th style={{ ...TH, textAlign: 'right' }}>Distance</th>
            <th style={{ ...TH, textAlign: 'right' }}>Deduction</th><th style={TH}>Status</th>
          </tr></thead>
          <tbody>
            {trips.map(t => {
              const s = TRIP_STATUS[t.status] ?? TRIP_STATUS.completed;
              const from = t.origin_address?.split(',')[0] ?? '—';
              const to = t.destination_address?.split(',')[0] ?? '—';
              return (
                <tr key={t.id}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 500 }}>{t.started_at?.split('T')[0] ?? '—'}</td>
                  <td style={TD}>{t.user_name ?? '—'}</td>
                  <td style={TD}>{t.vehicle_name ?? '—'}</td>
                  <td style={TD}>
                    <span style={{ fontSize: 12, color: palette.g600 }}>{from} → {to}</span>
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 500 }}>
                    {t.distance_km ? `${t.distance_km.toFixed(1)} km` : '—'}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', color: palette.green, fontWeight: 500 }}>
                    {t.mileage_deduction ? fmt$(t.mileage_deduction) : '—'}
                  </td>
                  <td style={TD}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: s.bg, color: s.fg }}>{s.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {loading && <div style={{ padding: 16, textAlign: 'center', color: palette.g400, fontSize: 12 }}>Loading...</div>}
      {hasMore && !loading && trips.length > 0 && (
        <button onClick={loadMore} style={{ width: '100%', padding: '10px 0', marginTop: 8, borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Load more
        </button>
      )}
    </div>
  );
}
